import React from 'react';
import { Button } from './ui/button';
import { Thermometer, AlertTriangle } from 'lucide-react';

interface TemperatureZone {
    id: string;
    name: string;
    actual: number;
    setpoint: number;
    unit: string;
    status: 'normal' | 'warning' | 'danger';
}

interface TemperatureControlDashboardProps {
    temperatureView: 'dashboard' | 'grid' | 'table' | 'warnings' | 'danger';
    onViewChange: (view: 'dashboard' | 'grid' | 'table' | 'warnings' | 'danger') => void;
    apiResponse: any;
    temperatureRanges?: {
        normal: {
            min: number;
            max: number;
        };
        warning: {
            min: number;
            max: number;
        };
    };
}

export const TemperatureControlDashboard: React.FC<TemperatureControlDashboardProps> = ({
    temperatureView,
    onViewChange,
    apiResponse,
    temperatureRanges,
}) => {
    // Helper function to calculate zone status based on diffValue and temperature thresholds
    const calculateZoneStatus = (
        diffValue: number | null,
        minTemp: number,
        maxTemp: number,
        underMinTemp: number,
        overMaxTemp: number
    ): 'normal' | 'warning' | 'danger' | 'unknown' => {
        if (diffValue === null) {
            return 'unknown';
        }

        // Danger zone: diffValue < under_minimum_temperature OR diffValue > over_maximum_temperature
        const isDanger = diffValue < underMinTemp || diffValue > overMaxTemp;
        // Normal zone: diffValue >= minimum_temperature AND diffValue <= maximum_temperature
        const isInNormalRange = diffValue >= minTemp && diffValue <= maxTemp;
        // Warning zone: (diffValue >= under_minimum_temperature AND diffValue < minimum_temperature) 
        //               OR (diffValue > maximum_temperature AND diffValue <= over_maximum_temperature)
        const isWarning = !isDanger && !isInNormalRange && 
            ((diffValue >= underMinTemp && diffValue < minTemp) || 
             (diffValue > maxTemp && diffValue <= overMaxTemp));

        if (isDanger) {
            return 'danger';
        } else if (isInNormalRange) {
            return 'normal';
        } else if (isWarning) {
            return 'warning';
        }
        return 'unknown';
    };

    return (
        <div className="mt-6 bg-white rounded-lg border border-gray-200">
            <div className="px-6 py-4 border-b border-gray-200">
                <div className="flex items-center justify-between">
                    <h3 className="text-lg font-medium text-gray-900 flex items-center gap-2">
                        <Thermometer className="h-5 w-5 text-purple-500" />
                        Temperature Control Dashboard
                        <span className="text-sm font-normal text-gray-500">({(apiResponse?.satDifferences && Array.isArray(apiResponse.satDifferences) ? apiResponse.satDifferences.length : 0)} zones)</span>
                    </h3>
                    <div className="flex gap-2">
                        <Button
                            variant={temperatureView === 'dashboard' ? 'default' : 'outline'}
                            size="sm"
                            onClick={() => onViewChange('dashboard')}
                            className="text-xs"
                        >
                            Dashboard
                        </Button>
                        <Button
                            variant={temperatureView === 'grid' ? 'default' : 'outline'}
                            size="sm"
                            onClick={() => onViewChange('grid')}
                            className="text-xs"
                        >
                            Grid
                        </Button>
                        <Button
                            variant={temperatureView === 'table' ? 'default' : 'outline'}
                            size="sm"
                            onClick={() => onViewChange('table')}
                            className="text-xs"
                        >
                            Table
                        </Button>
                        <Button
                            variant={temperatureView === 'warnings' ? 'default' : 'outline'}
                            size="sm"
                            onClick={() => onViewChange('warnings')}
                            className="text-xs"
                        >
                            <AlertTriangle className="h-3 w-3 mr-1" />
                            Warnings
                        </Button>
                        <Button
                            variant={temperatureView === 'danger' ? 'default' : 'outline'}
                            size="sm"
                            onClick={() => onViewChange('danger')}
                            className="text-xs bg-red-50 hover:bg-red-100"
                        >
                            <AlertTriangle className="h-3 w-3 mr-1 text-red-600" />
                            Danger
                        </Button>
                    </div>
                </div>
            </div>

            <div className="p-6">
                {/* Dashboard View */}
                {temperatureView === 'dashboard' && (() => {
                    // Get temperature range from API response, configured ranges, or use defaults
                    const minTemp = apiResponse?.minimum_temperature 
                        ?? temperatureRanges?.normal?.min 
                        ?? 245;
                    const maxTemp = apiResponse?.maximum_temperature 
                        ?? temperatureRanges?.normal?.max 
                        ?? 255;
                    const underMinTemp = apiResponse?.under_minimum_temperature ?? 0;
                    const overMaxTemp = apiResponse?.over_maximum_temperature ?? 0;
                    
                    // Calculate normal, warning, and danger zones based on temperature values
                    let normalZoneCount = 0;
                    let warningZoneCount = 0;
                    let dangerZoneCount = 0;
                    
                    if (apiResponse?.satDifferences && Array.isArray(apiResponse.satDifferences)) {
                        apiResponse.satDifferences.forEach((satData: any) => {
                            const satKey = satData?.sat_key || satData?.name || satData?.id || 'unknown';
                            const satNumber = satKey.toString().replace('sat_', '') || satData?.sat_number?.toString() || '';
                            const diffValueKey = `sat_difference_values_${satNumber}`;
                            const diffValue = satData?.[diffValueKey] ?? satData?.difference ?? satData?.delta ?? null;
                            
                            if (diffValue !== null) {
                                const status = calculateZoneStatus(diffValue, minTemp, maxTemp, underMinTemp, overMaxTemp);
                                
                                if (status === 'danger') {
                                    dangerZoneCount++;
                                } else if (status === 'normal') {
                                    normalZoneCount++;
                                } else if (status === 'warning') {
                                    warningZoneCount++;
                                }
                            }
                        });
                    }
                    
                    return (
                        <div className="space-y-6">
                            {/* Summary Cards */}
                            <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
                                <div className="text-center p-4 bg-green-50 rounded-lg border">
                                    <div className="text-2xl font-bold text-green-700">
                                        {normalZoneCount}
                                    </div>
                                    <div className="flex items-center justify-center gap-1 mt-2">
                                        <div className="h-2 w-2 bg-green-500 rounded-full"></div>
                                        <div className="text-xs text-green-600 font-medium">Normal Zones</div>
                                    </div>
                                </div>
                                <div className="text-center p-4 bg-yellow-50 rounded-lg border">
                                    <div className="text-2xl font-bold text-yellow-700">
                                        {warningZoneCount}
                                    </div>
                                    <div className="flex items-center justify-center gap-1 mt-2">
                                        <AlertTriangle className="h-3 w-3 text-yellow-600" />
                                        <div className="text-xs text-yellow-600 font-medium">Warning Zones</div>
                                    </div>
                                </div>
                                <div className="text-center p-4 bg-red-50 rounded-lg border">
                                    <div className="text-2xl font-bold text-red-700">
                                        {dangerZoneCount}
                                    </div>
                                    <div className="flex items-center justify-center gap-1 mt-2">
                                        <AlertTriangle className="h-3 w-3 text-red-600" />
                                        <div className="text-xs text-red-600 font-medium">Danger Zones</div>
                                    </div>
                                </div>
                            <div className="text-center p-4 bg-blue-50 rounded-lg border">
                                <div className="text-lg font-bold text-blue-700">
                                    {apiResponse?.hottest ?? 0}°C
                                </div>
                                <div className="flex items-center justify-center gap-1 mt-2">
                                    <span className="text-red-500">🔥</span>
                                    <div className="text-xs text-blue-600 font-medium">Hottest Zone</div>
                                </div>
                            </div>
                            <div className="text-center p-4 bg-purple-50 rounded-lg border">
                                <div className="text-lg font-bold text-purple-700">
                                    {apiResponse?.coolest ?? 0}°C
                                </div>
                                <div className="flex items-center justify-center gap-1 mt-2">
                                    <span className="text-blue-500">❄️</span>
                                    <div className="text-xs text-purple-600 font-medium">Coolest Zone</div>
                                </div>
                            </div>
                        </div>

                        {/* Zones Requiring Attention */}
                        {apiResponse?.satDifferences && Array.isArray(apiResponse.satDifferences) && (() => {
                            // Get temperature range from API response, configured ranges, or use defaults
                            const minTemp = apiResponse?.minimum_temperature 
                                ?? temperatureRanges?.normal?.min 
                                ?? 245;
                            const maxTemp = apiResponse?.maximum_temperature 
                                ?? temperatureRanges?.normal?.max 
                                ?? 255;
                            const underMinTemp = apiResponse?.under_minimum_temperature ?? 0;
                            const overMaxTemp = apiResponse?.over_maximum_temperature ?? 0;
                            
                            // Filter zones that are warnings or danger (outside normal range)
                            // Danger zones should also appear in warnings view
                            const warningZones = apiResponse.satDifferences
                                .map((satData: any, index: number) => {
                                    // Extract sat key from the object (could be sat_key, name, or similar)
                                    const satKey = satData?.sat_key || satData?.name || satData?.id || `sat_${index + 1}`;
                                    // Find sat number from key or from the object
                                    const satNumber = satKey.toString().replace('sat_', '') || satData?.sat_number?.toString() || (index + 1).toString();
                                    // Format display name as "SAT TC {number}"
                                    const displayName = `SAT TC ${satNumber}`;
                                    const satValueKey = `sat_values_${satNumber}`;
                                    const diffValueKey = `sat_difference_values_${satNumber}`;
                                    const satValue = satData?.[satValueKey] ?? satData?.sat_value ?? satData?.actual ?? null;
                                    const diffValue = satData?.[diffValueKey] ?? satData?.difference ?? satData?.delta ?? null;
                                    
                                    if (diffValue === null) {
                                        return null;
                                    }
                                    
                                    const status = calculateZoneStatus(diffValue, minTemp, maxTemp, underMinTemp, overMaxTemp);
                                    const isDanger = status === 'danger';
                                    const isInNormalRange = status === 'normal';
                                    
                                    // Include both warning and danger zones (danger zones should show in warnings view)
                                    if (isInNormalRange) {
                                        return null;
                                    }
                                    
                                    return { satKey, displayName, satValue, diffValue, isDanger };
                                })
                                .filter(Boolean);
                            
                            if (warningZones.length === 0) {
                                return null;
                            }
                            
                            return (
                                <div className="bg-yellow-50 border rounded-lg p-4">
                                    <div className="flex items-center gap-2 mb-3">
                                        <AlertTriangle className="h-4 w-4 text-yellow-600" />
                                        <h4 className="text-sm font-medium text-yellow-800">Zones Requiring Attention:</h4>
                                    </div>
                                    <div className="space-y-1">
                                        {warningZones.map((zone: any) => (
                                            <div key={zone.satKey} className={`text-sm ${zone.isDanger ? 'text-red-700 font-semibold' : 'text-gray-700'}`}>
                                                <span className="font-medium">{zone.displayName}:</span> {zone.satValue !== null ? `${zone.satValue}°C` : 'N/A'}
                                                <span className={zone.isDanger ? 'text-red-700' : 'text-yellow-700'}>
                                                    {' '}({zone.diffValue !== null ? (zone.diffValue > 0 ? '+' : '') + zone.diffValue : 'N/A'}° difference)
                                                    {zone.isDanger && ' ⚠️ DANGER'}
                                                </span>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            );
                        })()}
                        </div>
                    );
                })()}

                {/* Grid View */}
                {temperatureView === 'grid' && (
                    <div className="space-y-4">
                        <div className="text-center text-sm text-gray-500 mb-4">Temperature Zone Grid</div>
                        <div className="grid grid-cols-10 gap-2">
                            {apiResponse?.satDifferences && Array.isArray(apiResponse.satDifferences) && apiResponse.satDifferences.map((satData: any, index: number) => {
                                // Extract sat key from the object (could be sat_key, name, or similar)
                                const satKey = satData?.sat_key || satData?.name || satData?.id || `sat_${index + 1}`;
                                // Find sat number from key or from the object
                                const satNumber = satKey.toString().replace('sat_', '') || satData?.sat_number?.toString() || (index + 1).toString();
                                // Format display name as "SAT TC {number}"
                                const displayName = `SAT TC ${satNumber}`;
                                // Get the corresponding value keys
                                const satValueKey = `sat_values_${satNumber}`;
                                const diffValueKey = `sat_difference_values_${satNumber}`;
                                const satValue = satData?.[satValueKey] ?? satData?.sat_value ?? satData?.actual ?? null;
                                const diffValue = satData?.[diffValueKey] ?? satData?.difference ?? satData?.delta ?? null;
                                
                                // Get temperature range from API response, configured ranges, or use defaults
                                const minTemp = apiResponse?.minimum_temperature 
                                    ?? temperatureRanges?.normal?.min 
                                    ?? 245;
                                const maxTemp = apiResponse?.maximum_temperature 
                                    ?? temperatureRanges?.normal?.max 
                                    ?? 255;
                                const underMinTemp = apiResponse?.under_minimum_temperature ?? 0;
                                const overMaxTemp = apiResponse?.over_maximum_temperature ?? 0;
                                
                                // Determine zone status based on difference value
                                const status = calculateZoneStatus(diffValue, minTemp, maxTemp, underMinTemp, overMaxTemp);
                                const isDanger = status === 'danger';
                                const isInNormalRange = status === 'normal';
                                const isWarning = status === 'warning';
                                
                                return (
                                    <div key={satKey || index} className="text-center">
                                        <div className={`p-2 rounded text-xs font-mono border-2 ${
                                            satValue === null
                                                ? 'border-gray-300 bg-gray-100 text-gray-500'
                                                : isDanger
                                                    ? ' text-readable-red bg-[rgba(196,0,31,0.06)]'
                                                    : isWarning
                                                        ? ' text-[#B38600] bg-[rgba(230,161,0,0.08)]'
                                                        : ' text-galvanizing-green bg-[rgba(31,154,58,0.08)]'
                                        }`}>
                                            <div className="font-bold">{displayName}</div>
                                            <div className="text-xs">{satValue !== null ? `${satValue}°` : 'N/A'}</div>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                )}

                {/* Table View */}
                {temperatureView === 'table' && (
                    <div className="overflow-auto max-h-96">
                        <table className="min-w-full divide-y divide-gray-200">
                            <thead className="bg-gray-50 sticky top-0">
                                <tr>
                                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Zone</th>
                                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Actual</th>
                                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Set Point</th>
                                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Delta</th>
                                    <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">Status</th>
                                </tr>
                            </thead>
                            <tbody className="bg-white divide-y divide-gray-200">
                                {apiResponse?.satDifferences && Array.isArray(apiResponse.satDifferences) && apiResponse.satDifferences.map((satData: any, index: number) => {
                                    // Extract sat key from the object (could be sat_key, name, or similar)
                                    const satKey = satData?.sat_key || satData?.name || satData?.id || `sat_${index + 1}`;
                                    // Find sat number from key or from the object
                                    const satNumber = satKey.toString().replace('sat_', '') || satData?.sat_number?.toString() || (index + 1).toString();
                                    // Format display name as "SAT TC {number}"
                                    const displayName = `SAT TC ${satNumber}`;
                                    // Get the corresponding value keys
                                    const satValueKey = `sat_values_${satNumber}`;
                                    const satSetpointKey = `sat_setpoint_values_${satNumber}`;
                                    const satDiffKey = `sat_difference_values_${satNumber}`;
                                    
                                    const actual = satData?.[satValueKey] ?? satData?.sat_value ?? satData?.actual ?? null;
                                    const setpoint = satData?.[satSetpointKey] ?? satData?.setpoint ?? satData?.target ?? null;
                                    const delta = satData?.[satDiffKey] ?? satData?.difference ?? satData?.delta ?? null;
                                    
                                    // Get temperature range from API response, configured ranges, or use defaults
                                    const minTemp = apiResponse?.minimum_temperature 
                                        ?? temperatureRanges?.normal?.min 
                                        ?? 245;
                                    const maxTemp = apiResponse?.maximum_temperature 
                                        ?? temperatureRanges?.normal?.max 
                                        ?? 255;
                                    const underMinTemp = apiResponse?.under_minimum_temperature ?? 0;
                                    const overMaxTemp = apiResponse?.over_maximum_temperature ?? 0;
                                    
                                    // Determine zone status based on difference value (delta)
                                    const status = calculateZoneStatus(delta, minTemp, maxTemp, underMinTemp, overMaxTemp);
                                    
                                    return (
                                        <tr key={satKey || index}>
                                            <td className="px-4 py-3 text-sm font-medium text-gray-900">{displayName}</td>
                                            <td className="px-4 py-3 text-sm text-right font-mono text-gray-900">
                                                {actual !== null ? `${actual}°C` : 'N/A'}
                                            </td>
                                            <td className="px-4 py-3 text-sm text-right font-mono text-gray-600">
                                                {setpoint !== null ? `${setpoint}°C` : 'N/A'}
                                            </td>
                                            <td className={`px-4 py-3 text-sm text-right font-mono ${
                                                delta === null 
                                                    ? 'text-gray-500'
                                                    : delta > 0 
                                                        ? 'text-red-600' 
                                                        : 'text-blue-600'
                                            }`}>
                                                {delta !== null ? (delta > 0 ? '+' : '') + delta : 'N/A'}
                                            </td>
                                            <td className="px-4 py-3 text-center">
                                                <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                                                    status === 'normal'
                                                        ? 'text-galvanizing-green bg-[rgba(31,154,58,0.08)]'
                                                        : status === 'warning'
                                                            ? 'text-[#B38600] bg-[rgba(230,161,0,0.08)]'
                                                            : status === 'danger'
                                                                ? 'text-readable-red bg-[rgba(196,0,31,0.06)]'
                                                                : 'bg-gray-100 text-gray-800'
                                                }`}>
                                                    {status === 'normal' ? '🟢' : status === 'warning' ? '🟡' : status === 'danger' ? '🔴' : '⚪'}
                                                </span>
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                )}

                {/* Warnings View */}
                {temperatureView === 'warnings' && (() => {
                    // Get temperature range from API response, configured ranges, or use defaults
                    const minTemp = apiResponse?.minimum_temperature 
                        ?? temperatureRanges?.normal?.min 
                        ?? 245;
                    const maxTemp = apiResponse?.maximum_temperature 
                        ?? temperatureRanges?.normal?.max 
                        ?? 255;
                    const underMinTemp = apiResponse?.under_minimum_temperature ?? 0;
                    const overMaxTemp = apiResponse?.over_maximum_temperature ?? 0;
                    
                    // Calculate warning zones count
                    let warningZoneCount = 0;
                    if (apiResponse?.satDifferences && Array.isArray(apiResponse.satDifferences)) {
                        apiResponse.satDifferences.forEach((satData: any) => {
                            const satKey = satData?.sat_key || satData?.name || satData?.id || 'unknown';
                            const satNumber = satKey.toString().replace('sat_', '') || satData?.sat_number?.toString() || '';
                            const diffValueKey = `sat_difference_values_${satNumber}`;
                            const diffValue = satData?.[diffValueKey] ?? null;
                            
                            const status = calculateZoneStatus(diffValue, minTemp, maxTemp, underMinTemp, overMaxTemp);
                            
                            if (status === 'warning' || status === 'danger') {
                                warningZoneCount++;
                            }
                        });
                    }
                    
                    return (
                        <div className="space-y-3">
                            <h4 className="text-sm font-medium text-yellow-800 mb-3">
                                ⚠️ Temperature Zones with Warnings:
                            </h4>
                            {warningZoneCount === 0 ? (
                                <div className="text-center py-8 text-green-600">
                                    ✅ All zones operating normally
                                </div>
                            ) : (
                            apiResponse?.satDifferences && Array.isArray(apiResponse.satDifferences) && apiResponse.satDifferences
                                .map((satData: any, index: number) => {
                                    // Extract sat key from the object (could be sat_key, name, or similar)
                                    const satKey = satData?.sat_key || satData?.name || satData?.id || `sat_${index + 1}`;
                                    // Find sat number from key or from the object
                                    const satNumber = satKey.toString().replace('sat_', '') || satData?.sat_number?.toString() || (index + 1).toString();
                                    // Format display name as "SAT TC {number}"
                                    const displayName = `SAT TC ${satNumber}`;
                                    // Get the corresponding value keys
                                    const satValueKey = `sat_values_${satNumber}`;
                                    const satSetpointKey = `sat_setpoint_values_${satNumber}`;
                                    const satDiffKey = `sat_difference_values_${satNumber}`;
                                    
                                    const actual = satData?.[satValueKey] ?? satData?.sat_value ?? satData?.actual ?? null;
                                    const setpoint = satData?.[satSetpointKey] ?? satData?.setpoint ?? satData?.target ?? null;
                                    const delta = satData?.[satDiffKey] ?? satData?.difference ?? satData?.delta ?? null;
                                    
                                    // Get temperature range from API response, configured ranges, or use defaults
                                    const minTemp = apiResponse?.minimum_temperature 
                                        ?? temperatureRanges?.normal?.min 
                                        ?? 245;
                                    const maxTemp = apiResponse?.maximum_temperature 
                                        ?? temperatureRanges?.normal?.max 
                                        ?? 255;
                                    const underMinTemp = apiResponse?.under_minimum_temperature ?? 0;
                                    const overMaxTemp = apiResponse?.over_maximum_temperature ?? 0;
                                    
                                    // Determine zone status based on difference value (delta)
                                    const status = calculateZoneStatus(delta, minTemp, maxTemp, underMinTemp, overMaxTemp);
                                    const isDanger = status === 'danger';
                                    const isInNormalRange = status === 'normal';
                                    
                                    // Show both warning and danger zones (danger zones should appear in warnings view)
                                    if (isInNormalRange || delta === null) {
                                        return null;
                                    }
                                    
                                    return { satKey, displayName, actual, setpoint, delta, isDanger };
                                })
                                .filter(Boolean) // Remove null entries
                                .map((zone: any) => (
                                    <div key={zone.satKey} className={`p-3 rounded-lg border-2 ${zone.isDanger ? ' bg-red-50' : ' bg-yellow-50'}`}>
                                        <div className="flex items-center justify-between">
                                            <div>
                                                <div className={`font-medium ${zone.isDanger ? 'text-red-800' : 'text-yellow-800'}`}>
                                                    {zone.displayName} {zone.isDanger && '⚠️ DANGER'}
                                                </div>
                                                <div className={`text-sm ${zone.isDanger ? 'text-red-600' : 'text-yellow-600'}`}>
                                                    Current: {zone.actual !== null ? `${zone.actual}°C` : 'N/A'} | Target: {zone.setpoint !== null ? `${zone.setpoint}°C` : 'N/A'}
                                                </div>
                                            </div>
                                            <div className="text-right">
                                                <div className={`text-lg font-bold ${zone.isDanger ? 'text-red-700' : 'text-yellow-700'}`}>
                                                    {zone.delta !== null ? (zone.delta > 0 ? '+' : '') + zone.delta : 'N/A'}°C
                                                </div>
                                                <div className={`text-xs ${zone.isDanger ? 'text-red-600' : 'text-yellow-600'}`}>Deviation</div>
                                            </div>
                                        </div>
                                    </div>
                                ))
                            )}
                        </div>
                    );
                })()}

                {/* Danger View */}
                {temperatureView === 'danger' && (() => {
                    // Get temperature range from API response, configured ranges, or use defaults
                    const minTemp = apiResponse?.minimum_temperature 
                        ?? temperatureRanges?.normal?.min 
                        ?? 245;
                    const maxTemp = apiResponse?.maximum_temperature 
                        ?? temperatureRanges?.normal?.max 
                        ?? 255;
                    const underMinTemp = apiResponse?.under_minimum_temperature ?? 0;
                    const overMaxTemp = apiResponse?.over_maximum_temperature ?? 0;
                    
                    // Calculate danger zones count
                    let dangerZoneCount = 0;
                    if (apiResponse?.satDifferences && Array.isArray(apiResponse.satDifferences)) {
                        apiResponse.satDifferences.forEach((satData: any) => {
                            const satKey = satData?.sat_key || satData?.name || satData?.id || 'unknown';
                            const satNumber = satKey.toString().replace('sat_', '') || satData?.sat_number?.toString() || '';
                            const diffValueKey = `sat_difference_values_${satNumber}`;
                            const diffValue = satData?.[diffValueKey] ?? null;
                            
                            const status = calculateZoneStatus(diffValue, minTemp, maxTemp, underMinTemp, overMaxTemp);
                            
                            if (status === 'danger') {
                                dangerZoneCount++;
                            }
                        });
                    }
                    
                    return (
                        <div className="space-y-3">
                            <h4 className="text-sm font-medium text-red-800 mb-3">
                                🔴 Temperature Zones in Danger:
                            </h4>
                            {dangerZoneCount === 0 ? (
                                <div className="text-center py-8 text-green-600">
                                    ✅ No zones in danger
                                </div>
                            ) : (
                            apiResponse?.satDifferences && Array.isArray(apiResponse.satDifferences) && apiResponse.satDifferences
                                .map((satData: any, index: number) => {
                                    // Extract sat key from the object (could be sat_key, name, or similar)
                                    const satKey = satData?.sat_key || satData?.name || satData?.id || `sat_${index + 1}`;
                                    // Find sat number from key or from the object
                                    const satNumber = satKey.toString().replace('sat_', '') || satData?.sat_number?.toString() || (index + 1).toString();
                                    // Format display name as "SAT TC {number}"
                                    const displayName = `SAT TC ${satNumber}`;
                                    // Get the corresponding value keys
                                    const satValueKey = `sat_values_${satNumber}`;
                                    const satSetpointKey = `sat_setpoint_values_${satNumber}`;
                                    const satDiffKey = `sat_difference_values_${satNumber}`;
                                    
                                    const actual = satData?.[satValueKey] ?? satData?.sat_value ?? satData?.actual ?? null;
                                    const setpoint = satData?.[satSetpointKey] ?? satData?.setpoint ?? satData?.target ?? null;
                                    const delta = satData?.[satDiffKey] ?? satData?.difference ?? satData?.delta ?? null;
                                    
                                    // Get temperature range from API response, configured ranges, or use defaults
                                    const minTemp = apiResponse?.minimum_temperature 
                                        ?? temperatureRanges?.normal?.min 
                                        ?? 245;
                                    const maxTemp = apiResponse?.maximum_temperature 
                                        ?? temperatureRanges?.normal?.max 
                                        ?? 255;
                                    const underMinTemp = apiResponse?.under_minimum_temperature ?? 0;
                                    const overMaxTemp = apiResponse?.over_maximum_temperature ?? 0;
                                    
                                    // Determine zone status based on difference value (delta)
                                    const status = calculateZoneStatus(delta, minTemp, maxTemp, underMinTemp, overMaxTemp);
                                    const isDanger = status === 'danger';
                                    
                                    // Only show danger zones
                                    if (!isDanger || delta === null) {
                                        return null;
                                    }
                                    
                                    return (
                                        <div key={satKey || index} className="p-3 rounded-lg border-2 bg-red-50">
                                            <div className="flex items-center justify-between">
                                                <div>
                                                    <div className="font-medium text-red-800">
                                                        {displayName} ⚠️ DANGER
                                                    </div>
                                                    <div className="text-sm text-red-600">
                                                        Current: {actual !== null ? `${actual}°C` : 'N/A'} | Target: {setpoint !== null ? `${setpoint}°C` : 'N/A'}
                                                    </div>
                                                </div>
                                                <div className="text-right">
                                                    <div className="text-lg font-bold text-red-700">
                                                        {delta !== null ? (delta > 0 ? '+' : '') + delta : 'N/A'}°C
                                                    </div>
                                                    <div className="text-xs text-red-600">Deviation</div>
                                                </div>
                                            </div>
                                        </div>
                                    );
                                })
                                .filter(Boolean) // Remove null entries
                            )}
                        </div>
                    );
                })()}
            </div>
        </div>
    );
};

